import { useEffect, useState } from 'react';
import React from 'react';
import { Modal, Button, Spinner, Form, Row, Col } from 'react-bootstrap';
import { useAdminGetLiteral, useCreateLiteral } from '../../api-new';
import { useModifyLiteral } from '../../api-new';
import { getFormFields, getFormData, getRequestPayload, getDefaultFormData, validateFormData } from '../../utils/questionEditUtil';
import BookModalLink from "./BookModalLink";
import AlertDismissible from './AlertDismissible';
import Translated from "./Translated";
import { useTranslation } from './TranslationProvider';
import { useUserData } from '../../context/UserDataContext';
import { getSelectedAssessment } from '../../utils/assessmentUtil';

const QuestionContentModal = ({ questionId, onClose, onCloseAfterUpdate }) => {

  const { translate } = useTranslation();
  const { userSettings } = useUserData();
  const editMode = Boolean(questionId);
  const defaultFormData = getDefaultFormData();
  const formFields = getFormFields(editMode); 
  const [formData, setFormData] = useState(defaultFormData);
  const [modifiedFields, setModifiedFields] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [validationMessageVisible, setValidationMessageVisible] = useState(false);
  const selectedAssessment = getSelectedAssessment(userSettings.data, formData);

  const {
    data: questionData,
    isLoading: questionLoading,
    isError: fetchError,
  } = useAdminGetLiteral(questionId);

  const {
    mutate: updateLiteral,
    isPending: updatePending,
    isSuccess: updateSuccesful,
    isError: updateError
  } = useModifyLiteral();

  const {
    mutate: createLiteral,
    isPending: createPending,
    isSuccess: createSuccesful,
    isError: createError
  } = useCreateLiteral();

  useEffect(() => {
    if (questionData) {
      const newFormData = getFormData(questionData);
      setFormData(newFormData);
    }
  }, [questionData]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    const newFormData = { ...formData, [name]: type === 'checkbox' ? checked : value };
    setFormData(newFormData);
    if (editMode) {
      setModifiedFields(prev => prev.includes(name) ? prev : [...prev, name]);
    }
  };

const handleSubmit = () => {
  // Validate form data
  const { isValid, errors } = validateFormData(formData, selectedAssessment);
  if (!isValid) {
    setValidationErrors(errors);
    setValidationMessageVisible(!validationMessageVisible);
    return;
  }

  setValidationErrors({});
  const literalQuestion = getRequestPayload(formData);
  const action = editMode ? updateLiteral : createLiteral;

  action(literalQuestion, {
    onSuccess: () => {
      setModifiedFields([]);
      if (!editMode) {
        setFormData(defaultFormData);
      }
    }
  });
};


  const handleClose = () => {
    if (updateSuccesful || createSuccesful) {
      onCloseAfterUpdate();
    }
    onClose();
  };

  const renderField = (field) => {
    const isModified = modifiedFields.includes(field.name);
    switch (field.type) {
      case 'text':
        if (field.name === 'topic' && selectedAssessment) {
          return (
            <div>
              <Form.Select name={field.name} value={formData[field.name]} disabled={field.disabled} onChange={handleInputChange}>
                <option value="">{translate('select_topic')}</option>
                {selectedAssessment.topics.map(topic => (
                  <option value={topic.id} key={topic.id}>{translate(topic.localizationKey)}</option>
                ))}
              </Form.Select>
              {isModified && <span className="badge bg-warning"><Translated translationKey={'modified'} /></span>}
            </div>
          );
        }
        return (
          <div>
            <Form.Control
              type="text"
              name={field.name}
              value={formData[field.name]}
              disabled={field.disabled}
              onChange={handleInputChange}
            />
            {isModified && <span className="badge bg-warning"><Translated translationKey={'modified'} /></span>}
          </div>
        );

      case 'textarea':
        return (
          <div>
            <Form.Control
              as="textarea"
              name={field.name}
              rows={2}
              value={formData[field.name]}
              disabled={field.disabled}
              onChange={handleInputChange}
            />
            {isModified && <span className="badge bg-warning"><Translated translationKey={'modified'} /></span>}
          </div>
        );
      case 'checkbox':
        return (
          <Col xs="auto">
            <Form.Check className="fw-bold"
              type="checkbox"
              id={field.name}
              label={field.label}
              name={field.name}
              checked={formData[field.name]}
              disabled={field.disabled}
              onChange={handleInputChange}
            />
            {isModified && <span className="badge bg-warning"><Translated translationKey={'modified'} /></span>}
          </Col>
        );
      default:
        return null;
    }
  };

  const renderBookRefs = (refs) => {
    return refs.flatMap((ref) =>
      ref.languages.map((lang) => (  
        <div className="d-flex align-items-center gap-2" key={`${ref.id}-${lang}`}>
          <label className='fw-bold'>{lang}</label>
          <BookModalLink
            lang={lang}
            contentId={ref.id}
            showId
          />
        </div>
      ))
    );
  };

  const fetchPending = questionLoading ? (
    <Spinner animation="border" />
  ) : null;

  const fetchErrorAlert = fetchError ? (
    <AlertDismissible variant="danger" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <Translated translationKey={"error_fetching_data"} />
    </AlertDismissible>
  ) : null;

  const succesfulUpdateAlert = updateSuccesful ? (
    <AlertDismissible variant="success" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <Translated translationKey={"updated_successfully"} />
    </AlertDismissible>
  ) : null;

  const errorUpdateAlert = updateError ? (
    <AlertDismissible variant="danger" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <Translated translationKey={"update_failed"} />
    </AlertDismissible>
  ) : null;

  const succesfulCreateAlert = createSuccesful ? (
    <AlertDismissible variant="success" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <Translated translationKey={"literal_create_success"} />
    </AlertDismissible>
  ) : null;

  const errorCreateAlert = createError ? (
    <AlertDismissible variant="danger" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <Translated translationKey={"literal_create_fail"} />
    </AlertDismissible>
  ) : null;

  const validationErrorAlert = Object.keys(validationErrors).length > 0 ? (
    <AlertDismissible 
      variant="danger" 
      style={{ position: 'sticky', top: 0, zIndex: 10 }}
      key={validationMessageVisible}
    >
      <div>
        <strong><Translated translationKey={'validation_errors'} />:</strong>
        <ul className="mb-0">
          {Object.entries(validationErrors).map(([field, translationKey]) => (
            <li key={field}><Translated translationKey={translationKey} /></li>
          ))}
        </ul>
      </div>
    </AlertDismissible>
  ) : null;

  const licenseCategories = (
    <div className="mb-2">
      <label className="fw-bold">
        <Translated translationKey={'license_class'} />:
      </label>
    </div>
  );


  return (
    <Modal show onHide={handleClose} size="lg" backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title><Translated translationKey={editMode ? "edit" : "create_literal"} /></Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {fetchPending}
        {fetchErrorAlert}
        {validationErrorAlert}
        {succesfulUpdateAlert}
        {errorUpdateAlert}
        {succesfulCreateAlert}
        {errorCreateAlert}

        {formData && (
          <>
            <Form>
              <Form.Group as={Row} className="mb-4">
                {formFields
                  .filter(f => f.type === 'checkbox' && !f.group)
                  .map((field) => (
                    <React.Fragment key={field.name}>
                      {renderField(field)}
                    </React.Fragment>
                  ))}
              </Form.Group>
              
              {licenseCategories}

              <Form.Group as={Row} className="mb-4">
                {formFields
                  .filter(f => f.type === 'checkbox' && f.group === 'category')
                  .map((field) => (
                    <React.Fragment key={field.name}>
                      {renderField(field)}
                    </React.Fragment>
                  ))}
              </Form.Group>

              {formFields
                .filter(f => f.type !== 'checkbox')
                .map((field) => (
                  <Form.Group key={field.name} className="mb-2">
                    <Form.Label className="fw-bold">{field.label}</Form.Label>
                    {renderField(field)}
                  </Form.Group>
                ))}
            </Form>

            {questionData?.bookReferences.length > 0 &&
              <div className="mb-4">
                <Translated translationKey={"book_references"} /> (<Translated translationKey={"not_editable"} />):
                {renderBookRefs(questionData.bookReferences)}
              </div>
            }
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          <Translated translationKey={'cancel'} />
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={questionLoading || updatePending || createPending}>
          <Translated translationKey={'save'} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionContentModal;
